import {
  createContext,
  use,
  useCallback,
  useState,
  type ReactNode,
} from "react";
import { Trans } from "react-i18next";
import {
  Check,
  FileCheck,
  Hourglass,
  LoaderCircle,
  Mail,
  TriangleAlert,
} from "lucide-react";

import type { KioskAudioSlotKey } from "@common/kiosk-audio/slots";
import type { GuideStatus, GuideStepSpec } from "@frontend-common/start-guide/start-guide-logic";
import { useGuideSteps } from "@frontend-common/start-guide/use-guide-steps";
import { cn } from "@/lib/utils";
import type { CollectorsDiscountData } from "@/hooks/use-apply-payment";
import { AudioSlot } from "@/features/audio/kiosk-audio";
import { CollectorsDiscountCard } from "@/features/wash-start-guide/collectors-discount-card";
import { FlowBack } from "@/features/payment-flow/flow-header";

// Pieces shared by the washer and dryer start guides.

// Once the machine runs, the guide fades out (its drum is spinning up in the
// scene) and the success view rises in. Starts just before the scene's 3.7 s
// spin-up ends, so the drum is still turning at full speed while it fades.
export const GUIDE_EXIT = "guide-exit 600ms ease-in 3.2s forwards";

// the washer and dryer scenes are drawn on a stage this big
export const SCENE_STAGE = { width: 960, height: 560 };

// The whole room the scene got, in the scene's own pixels (never smaller than
// the stage), for a scene that wants to fill it instead of keeping the stage.
// `freeAbove[view]`: the text block is as tall as the longest step's text, so
// a step with less text leaves this much unused right above the room; a scene
// may draw into it while showing that view.
export type SceneRoom = {
  width: number;
  height: number;
  freeAbove: Partial<Record<string, number>>;
};

const SceneRoomContext = createContext<SceneRoom>({
  ...SCENE_STAGE,
  freeAbove: {},
});

export function useSceneRoom() {
  return use(SceneRoomContext);
}

export function StepDots({ count, active }: { count: number; active: number }) {
  return (
    <div className="flex gap-3">
      {Array.from({ length: count }, (_, index) => (
        <span
          key={index}
          className={cn(
            "h-4 rounded-full transition-all",
            index === active ? "w-16 bg-bub-orange" : "w-4",
            index < active && "bg-bub-orange/50",
            index > active && "bg-bub-dark-blue/20",
          )}
        />
      ))}
    </div>
  );
}

export function ConfirmButton({
  onClick,
  children,
}: {
  onClick: () => void;
  children: ReactNode;
}) {
  return (
    <button
      onClick={onClick}
      className="bg-bub-orange text-white rounded-full px-16 pt-6 pb-5 text-[44px] font-fancy font-bold shadow-lg active:scale-95 transition-transform"
    >
      {children}
    </button>
  );
}

export function WaitingForStart({ machineNumber }: { machineNumber: number }) {
  return (
    <div className="w-full flex items-center justify-center gap-5 rounded-3xl bg-bub-vivid-blue/10 px-10 py-7">
      <LoaderCircle className="size-14 animate-spin" stroke="#009dc9" />
      <p className="text-[32px] font-bold">
        <Trans values={{ machineNumber }}>
          Várjuk, hogy a(z) {{ machineNumber }}. gép elinduljon…
        </Trans>
      </p>
    </div>
  );
}

// replaces the waiting box while the drum spins up, before the success view
// Thin bar at the bottom of the guide: if the machine isn't running by the
// time it empties, the guide gives up and shows the failure screen. Mounted
// with the guide, so it counts from payment, across steps. Freezes once the
// machine runs.
export function StartTimeoutBar({
  seconds,
  stopped,
  onEnd,
  caption,
}: {
  seconds: number;
  stopped: boolean;
  onEnd: () => void;
  // what the machine waits for; no time in it, the bar shows that
  caption: ReactNode;
}) {
  return (
    // faint on purpose: most people won't notice it, but the caption explains
    // the bar to anyone who does
    <div className="relative z-20 mt-6 w-full flex flex-col items-center gap-2 opacity-60">
      <div className="w-full h-1.5 rounded-full bg-bub-dark-blue/10 overflow-hidden">
        <div
          className="h-full bg-bub-dark-blue/30 origin-left"
          style={{
            animation: `guide-countdown ${seconds}s linear both`,
            animationPlayState: stopped ? "paused" : "running",
          }}
          onAnimationEnd={(event) => {
            if (event.animationName === "guide-countdown") onEnd();
          }}
        />
      </div>
      <span className="flex items-center gap-2 text-[18px]">
        <Hourglass className="size-5" stroke="#005D86" />
        {caption}
      </span>
    </div>
  );
}

export function StartedBanner({ machineNumber }: { machineNumber: number }) {
  return (
    <div className="w-full flex items-center justify-center gap-5 rounded-3xl bg-bub-vegan/15 px-10 py-7 animate-[guide-pop_400ms_ease-out]">
      <span className="size-14 rounded-full bg-bub-vegan flex items-center justify-center">
        <Check className="size-10" stroke="white" strokeWidth={3} />
      </span>
      <p className="text-[32px] font-bold">
        <Trans values={{ machineNumber }}>
          A(z) {{ machineNumber }}. gép elindult!
        </Trans>
      </p>
    </div>
  );
}

export {
  guideStatus,
  type GuidePhase,
  type GuideStatus,
} from "@frontend-common/start-guide/start-guide-logic";

// The kiosk's texts for one step of the shared step specs
// (`@frontend-common/start-guide/start-guide-logic`).
export type StepTexts = {
  title: ReactNode;
  description?: ReactNode;
  warning?: ReactNode;
  // a warning the scene shows next to its drawing (the scene's caller passes
  // it on), for steps where above it would leave the drawing too little room
  sceneNote?: ReactNode;
  // the spec's `confirm` steps only
  confirmLabel?: ReactNode;
  audioSlot?: KioskAudioSlotKey;
};

export type GuideStep<View extends string> = GuideStepSpec<View> & StepTexts;

/**
 * The steps of a start guide: text, the machine's scene, and a confirm button
 * (or the wait for the machine). Once `isRunning`, it fades out while the
 * scene's drum spins up and then calls `onSpinUpEnd`.
 */
export function GuideSteps<View extends string>({
  steps,
  scene,
  showStartTimeout,
  onStepConfirm,
  onLastConfirm,
  onBackFromFirst,
  machineNumber,
  isRunning,
  onSpinUpEnd,
  startTimeoutSeconds,
  onStartTimeout,
  showDots = steps.length > 1,
  startTimeoutCaption,
  onPaidBeatEnd,
}: {
  steps: GuideStep<View>[];
  // the scene stays mounted across steps, so its camera can move
  scene: (stepIndex: number) => ReactNode;
  showStartTimeout: boolean;
  onStepConfirm?: (step: GuideStep<View>) => void;
  onLastConfirm?: () => void;
  onBackFromFirst?: () => void;
  machineNumber: number;
  isRunning: boolean;
  onSpinUpEnd: () => void;
  startTimeoutSeconds: number;
  onStartTimeout: () => void;
  showDots?: boolean;
  startTimeoutCaption: ReactNode;
  // set right after payment: "Sikeres fizetés!" covers the step text and the
  // scene for a moment, the waiting box below already in its place
  onPaidBeatEnd?: () => void;
}) {
  const { stepIndex, step, canGoBack, back, advance, confirm } = useGuideSteps({
    steps,
    onStepConfirm,
    onLastConfirm,
  });

  return (
    <div
      className="relative flex-1 flex flex-col items-center text-center mt-10"
      // once running: fade out while the drum is still spinning, then hand
      // over to the success view
      style={{ animation: isRunning ? GUIDE_EXIT : undefined }}
      onAnimationEnd={(event) => {
        if (event.animationName === "guide-exit") onSpinUpEnd();
      }}
    >
      {step.audioSlot && <AudioSlot slot={step.audioSlot} />}
      {!!step.advanceAfterSeconds && !isRunning && (
        <AdvanceTimer
          // a new timer per step
          key={step.id}
          seconds={step.advanceAfterSeconds}
          onEnd={advance}
        />
      )}
      {canGoBack && !isRunning ? (
        <FlowBack onClick={back} />
      ) : (
        onBackFromFirst && <FlowBack onClick={onBackFromFirst} />
      )}
      {showDots && <StepDots count={steps.length} active={stepIndex} />}
      <StepTextsAndScene steps={steps} stepIndex={stepIndex}>
        {scene(stepIndex)}
      </StepTextsAndScene>

      {onPaidBeatEnd && <PaidBeat onEnd={onPaidBeatEnd} />}
      <div className="relative z-20 mt-auto w-full flex flex-col items-center gap-6">
        {isRunning ? (
          <StartedBanner machineNumber={machineNumber} />
        ) : step.confirm ? (
          <ConfirmButton onClick={confirm}>
            {step.confirmLabel}
          </ConfirmButton>
        ) : (
          <WaitingForStart machineNumber={machineNumber} />
        )}
      </div>
      {showStartTimeout && (
        <StartTimeoutBar
          seconds={startTimeoutSeconds}
          stopped={isRunning}
          onEnd={onStartTimeout}
          caption={startTimeoutCaption}
        />
      )}
    </div>
  );
}

// how long "Sikeres fizetés!" covers the guide, fade-out included: short, so
// it isn't read as the end of the flow
const PAID_BEAT_SECONDS = 1.5;

// Covers the guide's text and scene (not the waiting box or the timeout bar
// below them), then fades out by itself and calls `onEnd`. Starts 40px down:
// the guide reaches up into the header's wave, which stays visible.
function PaidBeat({ onEnd }: { onEnd: () => void }) {
  return (
    <div
      className="absolute inset-x-0 top-10 bottom-0 z-10 flex flex-col items-center justify-center pb-48 bg-gray-50"
      style={{
        animation: `guide-paid-beat ${PAID_BEAT_SECONDS}s ease-in both`,
      }}
      onAnimationEnd={(event) => {
        if (event.animationName === "guide-paid-beat") onEnd();
      }}
    >
      <div className="size-[120px] rounded-full bg-bub-vegan flex items-center justify-center shadow-xl animate-[guide-pop_500ms_ease-out_both]">
        <Check className="size-[80px]" stroke="white" strokeWidth={3} />
      </div>
      <p className="text-[48px] font-fancy font-bold mt-8 leading-tight animate-[guide-rise_500ms_ease-out_200ms_both]">
        <Trans>Sikeres fizetés!</Trans>
      </p>
    </div>
  );
}

// An invisible CSS countdown, so the timer needs no effect: like the start
// timeout bar, it calls `onEnd` when its animation ends.
function AdvanceTimer({
  seconds,
  onEnd,
}: {
  seconds: number;
  onEnd: () => void;
}) {
  return (
    <span
      aria-hidden
      className="absolute size-0"
      style={{ animation: `guide-countdown ${seconds}s linear both` }}
      onAnimationEnd={(event) => {
        event.stopPropagation();
        if (event.animationName === "guide-countdown") onEnd();
      }}
    />
  );
}

export function GuideWarning({
  className,
  children,
}: {
  className?: string;
  children: ReactNode;
}) {
  return (
    <p
      className={cn(
        "flex items-center gap-3 rounded-2xl bg-bub-orange/15 px-6 py-3 text-left text-[26px] font-bold text-bub-orange",
        className,
      )}
    >
      <TriangleAlert className="size-8 shrink-0" />
      {children}
    </p>
  );
}

// Every step's text sits in the same grid cell, so the block is as tall as
// the longest one and the scene below never jumps between steps. What a
// shorter text leaves free is handed to the scene (`SceneRoom.freeAbove`).
function StepTextsAndScene<View extends string>({
  steps,
  stepIndex,
  children,
}: {
  steps: GuideStep<View>[];
  stepIndex: number;
  children: ReactNode;
}) {
  const [heights, setHeights] = useState<Partial<Record<string, number>>>({});
  const tallest = Math.max(0, ...Object.values(heights).map(Number));
  const freeAbove = Object.fromEntries(
    steps.map((step) => [step.view, tallest - (heights[step.id] ?? tallest)]),
  );
  return (
    <>
      <div className="grid">
        {steps.map((step, index) => (
          <StepText
            key={step.id}
            step={step}
            current={index === stepIndex}
            onHeight={(height) =>
              setHeights((previous) =>
                previous[step.id] === height
                  ? previous
                  : { ...previous, [step.id]: height },
              )
            }
          />
        ))}
      </div>
      <FittedScene freeAbove={freeAbove}>{children}</FittedScene>
    </>
  );
}

// crossfades with the other steps' text in the same grid cell
function StepText<View extends string>({
  step,
  current,
  onHeight,
}: {
  step: GuideStep<View>;
  current: boolean;
  onHeight: (height: number) => void;
}) {
  const measure = useCallback(
    (element: HTMLDivElement | null) => {
      if (!element) return;
      const observer = new ResizeObserver(() => onHeight(element.offsetHeight));
      observer.observe(element);
      return () => observer.disconnect();
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps -- set once per step
    [],
  );
  return (
    <div
      ref={measure}
      aria-hidden={!current}
      className={cn(
        // own height (not the row's), so it can be measured
        "[grid-area:1/1] self-start flex flex-col items-center transition-[opacity,translate,visibility] duration-300 ease-out",
        !current && "invisible opacity-0 translate-y-3",
      )}
    >
      <p className="text-[48px] font-fancy font-bold leading-tight max-w-[880px] mt-8">
        {step.title}
      </p>
      {step.description && (
        <p className="text-[28px] mt-4 max-w-[900px] text-bub-dark-blue/80">
          {step.description}
        </p>
      )}
      {step.warning && (
        <GuideWarning className="mt-4 max-w-[960px]">{step.warning}</GuideWarning>
      )}
    </div>
  );
}

/**
 * The room between a step's text and its button. The scene keeps its size
 * when it fits, and shrinks when it doesn't (small monitors, a long warning),
 * so the button and the countdown below stay on screen. `useSceneRoom()`
 * gives the scene the whole room.
 */
function FittedScene({
  freeAbove,
  children,
}: {
  // in page pixels, see `SceneRoom`
  freeAbove: Partial<Record<string, number>>;
  children: ReactNode;
}) {
  const [room, setRoom] = useState<{ width: number; height: number }>(SCENE_STAGE);
  const fit = useCallback((element: HTMLDivElement | null) => {
    if (!element) return;
    const observer = new ResizeObserver(([entry]) => {
      const { width, height } = entry!.contentRect;
      setRoom({ width, height });
    });
    observer.observe(element);
    return () => observer.disconnect();
  }, []);
  const scale = Math.min(
    1,
    room.width / SCENE_STAGE.width,
    room.height / SCENE_STAGE.height,
  );

  return (
    // the scene is out of the flow, so this only gets the room that's left
    <div ref={fit} className="relative mt-8 mb-6 w-full flex-1 min-h-0">
      <div className="absolute inset-x-0 top-0 flex justify-center">
        <div style={{ zoom: scale }}>
          <SceneRoomContext
            value={{
              width: room.width / scale,
              height: room.height / scale,
              freeAbove: Object.fromEntries(
                Object.entries(freeAbove).map(([view, free]) => [
                  view,
                  free! / scale,
                ]),
              ),
            }}
          >
            {children}
          </SceneRoomContext>
        </div>
      </div>
    </div>
  );
}

/** What the flow header says during the start guide. */
export function guideHeader(status: GuideStatus) {
  return {
    // "prepare": before payment (dryer); "starting": after it, nothing left to do
    eyebrow:
      status === "prepare" ? (
        <Trans>Fizetés előtt</Trans>
      ) : (
        <Trans>Sikeres fizetés!</Trans>
      ),
    title:
      status === "running" ? (
        <Trans>Köszönjük!</Trans>
      ) : status === "failed" ? (
        <Trans>Hoppá, valami elakadt</Trans>
      ) : status === "prepare" ? (
        <Trans>Készítsd elő a gépet!</Trans>
      ) : status === "starting" ? (
        <Trans>Indul a gép…</Trans>
      ) : (
        <Trans>Most indítsd el a gépet!</Trans>
      ),
    titleKey: `guide-${status}`,
    tone: status === "running" ? ("running" as const) : ("default" as const),
  };
}

export type InvoiceResult = "sent" | "queued" | "error";

// Passed when the purchase can get an invoice (old invoice-question-screen
// rules: not logged in, price > 0, shop can generate invoices).
export type InvoiceOffer = {
  // set when we're back from the invoice form
  result?: InvoiceResult;
  // the question skips itself after this long (the old screen used 30 s)
  questionSeconds?: number;
  // "Igen": open the invoice form
  onRequest?: () => void;
};

// Merged with the old "Köszönjük" (bubbles-loves-you) screen and the invoice
// question screen. First the invoice question (if offered); after "Nem" or its
// countdown: the collectors discount, the activation reminder and the "start
// another machine?" question, inline instead of in back-to-back modals.
export function RunningState({
  machineNumber,
  wish,
  invoice,
  isNewRegistration,
  collectorsDiscount,
  currencySymbol,
  returnAfterSeconds,
  onStartAnother,
  onFinish,
}: {
  machineNumber: number;
  // "Jó mosást…" / "Jó szárítást…"
  wish: ReactNode;
  invoice?: InvoiceOffer;
  isNewRegistration: boolean;
  collectorsDiscount?: CollectorsDiscountData;
  currencySymbol: string;
  returnAfterSeconds: number;
  onStartAnother?: () => void;
  onFinish?: () => void;
}) {
  const [invoiceSkipped, setInvoiceSkipped] = useState(false);
  const askInvoice = !!invoice && !invoice.result && !invoiceSkipped;

  return (
    <div className="flex-1 flex flex-col items-center text-center mt-10 animate-[guide-rise_600ms_ease-out]">
      <div className="size-[140px] rounded-full bg-bub-vegan flex items-center justify-center shadow-xl animate-[guide-pop_500ms_ease-out_150ms_both]">
        <Check className="size-[96px]" stroke="white" strokeWidth={3} />
      </div>
      <p className="text-[60px] font-fancy font-bold mt-6 leading-tight">
        <Trans values={{ machineNumber }}>
          A(z) {{ machineNumber }}. gép elindult!
        </Trans>
      </p>
      <p className="text-[32px] mt-3">{wish}</p>
      {/* the illustration makes room when there's more to read */}
      {(askInvoice || !collectorsDiscount) && (
        <img src="/bubbles-loves-you.svg" className="w-[400px] mt-4" />
      )}

      {askInvoice ? (
        <ChoiceCard
          key="invoice"
          question={
            <Trans>Szeretnél számlát kérni ehhez a tranzakcióhoz?</Trans>
          }
          noLabel={<Trans>Nem, köszönöm</Trans>}
          yesLabel={<Trans>Igen, kérek számlát</Trans>}
          onNo={() => setInvoiceSkipped(true)}
          onYes={invoice.onRequest}
          seconds={invoice.questionSeconds ?? 30}
          note={<Trans>Ha nem választasz, számla nélkül folytatjuk.</Trans>}
        />
      ) : (
        // rises in when it replaces the invoice question
        <div
          key="after-invoice"
          className={cn(
            "flex-1 w-full flex flex-col",
            invoiceSkipped && "animate-[guide-rise_500ms_ease-out]",
          )}
        >
          {invoice?.result && <InvoiceResultCard result={invoice.result} />}

          {collectorsDiscount && (
            <div className="mt-6 w-full">
              <CollectorsDiscountCard
                data={collectorsDiscount}
                currencySymbol={currencySymbol}
              />
            </div>
          )}

          {isNewRegistration && (
            <div className="mt-4 w-full rounded-3xl bg-bub-vivid-blue/10 px-10 py-5 text-left flex items-center gap-6">
              <Mail className="size-14 shrink-0" stroke="#009dc9" />
              <p className="text-[26px]">
                <Trans>
                  <b>Aktiváld a fiókod!</b> A következő belépés előtt
                  kattints az „Aktiváld Bubbles fiókod” tárgyú emailben kapott
                  linkre.
                </Trans>
              </p>
            </div>
          )}

          <ChoiceCard
            key="start-another"
            question={<Trans>Indítasz még egy gépet?</Trans>}
            noLabel={<Trans>Nem, köszönöm</Trans>}
            yesLabel={<Trans>Igen, még egyet</Trans>}
            onNo={onFinish}
            onYes={onStartAnother}
            seconds={returnAfterSeconds}
            note={
              <Trans>A kioszk hamarosan visszaáll a kezdőképernyőre.</Trans>
            }
          />
        </div>
      )}
    </div>
  );
}

// Question with no / yes buttons. The CSS countdown bar counts as "no" when it
// empties, so no timer is needed.
function ChoiceCard({
  question,
  noLabel,
  yesLabel,
  onNo,
  onYes,
  seconds,
  note,
}: {
  question: ReactNode;
  noLabel: ReactNode;
  yesLabel: ReactNode;
  onNo?: () => void;
  onYes?: () => void;
  seconds: number;
  note: ReactNode;
}) {
  return (
    <div className="mt-auto w-full rounded-3xl bg-white shadow-lg px-10 pt-6 pb-5">
      <p className="text-[40px] font-fancy font-bold">{question}</p>
      <div className="flex justify-center gap-6 mt-6">
        <button
          onClick={onNo}
          className="rounded-full border-4 border-bub-dark-blue/30 px-12 pt-5 pb-4 text-[36px] font-fancy font-bold active:scale-95 transition-transform"
        >
          {noLabel}
        </button>
        <button
          onClick={onYes}
          className="bg-bub-orange text-white rounded-full px-12 pt-5 pb-4 text-[36px] font-fancy font-bold shadow-lg active:scale-95 transition-transform"
        >
          {yesLabel}
        </button>
      </div>
      <div className="mt-6 h-2 rounded-full bg-bub-dark-blue/10 overflow-hidden">
        <div
          className="h-full bg-bub-dark-blue/40 origin-left"
          style={{ animation: `guide-countdown ${seconds}s linear both` }}
          onAnimationEnd={onNo}
        />
      </div>
      <p className="text-[20px] mt-2 text-bub-dark-blue/60">{note}</p>
    </div>
  );
}

// same messages as the old InvoiceSentModal / InvoiceErrorModal
function InvoiceResultCard({ result }: { result: InvoiceResult }) {
  const failed = result === "error";
  return (
    <div
      className={cn(
        "mt-6 w-full rounded-3xl px-10 py-5 text-left flex items-center gap-6",
        failed ? "bg-bub-orange/10" : "bg-bub-vegan/10",
      )}
    >
      {failed ? (
        <TriangleAlert className="size-14 shrink-0" stroke="#f45e22" />
      ) : (
        <FileCheck className="size-14 shrink-0" stroke="#00aa2b" />
      )}
      <p className="text-[26px]">
        {result === "sent" && (
          <Trans>A számlát elküldtük a megadott e-mail címre</Trans>
        )}
        {result === "queued" && (
          <Trans>
            A számlakérést rögzítettük. A számlát a megadott e-mail címre
            küldjük, amint helyreáll az internetkapcsolat
          </Trans>
        )}
        {failed && <Trans>Hiba történt a számla generálása közben</Trans>}
      </p>
    </div>
  );
}

export function GuideKeyframes() {
  return (
    <style>{`
      @keyframes guide-enter {
        0% { opacity: 0; transform: translateX(40px); }
        100% { opacity: 1; transform: none; }
      }
      @keyframes guide-countdown {
        from { transform: scaleX(1); }
        to { transform: scaleX(0); }
      }
      @keyframes guide-exit {
        to { opacity: 0; transform: scale(.96); }
      }
      @keyframes guide-rise {
        from { opacity: 0; transform: translateY(40px); }
        to { opacity: 1; transform: none; }
      }
      @keyframes flow-enter {
        from { opacity: 0; transform: translateX(80px); }
        to { opacity: 1; transform: none; }
      }
      @keyframes flow-exit {
        to { opacity: 0; transform: translateX(-80px); }
      }
      @keyframes flow-enter-back {
        from { opacity: 0; transform: translateX(-80px); }
        to { opacity: 1; transform: none; }
      }
      @keyframes flow-exit-back {
        to { opacity: 0; transform: translateX(80px); }
      }
      @keyframes guide-paid-beat {
        0%, 75% { opacity: 1; }
        100% { opacity: 0; }
      }
      @keyframes guide-pop {
        0% { transform: scale(.4); opacity: 0; }
        80% { transform: scale(1.08); opacity: 1; }
        100% { transform: scale(1); }
      }
    `}</style>
  );
}
